import { useState } from "react";
import { MerchantOverrides } from "../useReview/use-merchant-overrides";

export enum AdjustmentType {
  FEE = "Fee",
  BONUS = "Bonus",
}

export enum AdjustmentModeType {
  AMOUNT = "Amount",
  PERCENTAGE = "Percentage",
}

export const useItemAdjustments = ({
  overrides,
  pendingReturnItemId,
  itemReturnValue,
  onClose,
}: {
  overrides: NonNullable<MerchantOverrides>;
  pendingReturnItemId: string;
  itemReturnValue: number;
  onClose: () => void;
}) => {
  const adjustmentOverrideValue =
    overrides?.inputs?.adjustmentOverrides.get(pendingReturnItemId) ?? 0;
  const [adjustmentType, setAdjustmentType] = useState<AdjustmentType>(
    adjustmentOverrideValue > 0 ? AdjustmentType.BONUS : AdjustmentType.FEE,
  );
  const [adjustmentMode, setAdjustmentMode] = useState<AdjustmentModeType>(
    AdjustmentModeType.AMOUNT,
  );

  const [inputValue, setInputValue] = useState<string>(
    (adjustmentOverrideValue !== 0
      ? Math.abs(adjustmentOverrideValue)
      : 0
    ).toFixed(2),
  );

  const onApply = (
    adjustmentType: AdjustmentType,
    adjustmentMode: AdjustmentModeType,
  ) => {
    const value = parseFloat(inputValue) || 0;
    const baseValue = itemReturnValue ?? 0;

    // Calculate final amount based on mode
    let finalAmount: number;
    if (adjustmentMode === AdjustmentModeType.PERCENTAGE) {
      finalAmount = (baseValue * value) / 100;
    } else {
      finalAmount = value;
    }

    // Ensure proper 2 decimal place precision for monetary values
    finalAmount = Math.round(finalAmount * 100) / 100;

    overrides.inputs.onChangeAdjustmentOverride(
      pendingReturnItemId,
      adjustmentType === AdjustmentType.BONUS ? finalAmount : -finalAmount,
    );
    onClose();
  };

  const formatInputValue = (value: string) => {
    if (!value || isNaN(parseFloat(value))) {
      setInputValue("0.00");
    } else {
      const parsedValue = parseFloat(value);
      setInputValue(parsedValue >= 0 ? parsedValue.toFixed(2) : "0.00");
    }
  };

  return {
    adjustmentType,
    adjustmentMode,
    adjustmentValue: inputValue,
    onAdjustmentValueChange: (value: string) => setInputValue(value),
    isInputValid: !isNaN(parseFloat(inputValue)),
    onApply,
    formatInputValue,
    onAdjustmentTypeChange: (type: string) =>
      setAdjustmentType(type as AdjustmentType),
    onAdjustmentModeChange: (mode: string) =>
      setAdjustmentMode(mode as AdjustmentModeType),
  };
};
